<template>
  <!-- 플로팅 채팅 버튼 -->
  <div class="fixed bottom-6 right-6 z-50">
    <!-- 채팅창 (열렸을 때) -->
    <transition name="chat-popup">
      <div
        v-if="isOpen"
        class="absolute bottom-20 right-0 w-80 h-[480px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      >
        <!-- 헤더 -->
        <div class="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
            </div>
            <div>
              <p class="text-[14px] font-bold text-white">파트너스 톡</p>
              <p class="text-[10px] text-white/70">파트너 전용 채팅</p>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <router-link
              to="/partners/chat"
              class="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="전체 화면으로 보기"
              @click="isOpen = false"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
              </svg>
            </router-link>
            <button
              @click="isOpen = false"
              class="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title="닫기"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- 채팅 영역 -->
        <!-- 메시지 목록 -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
          <div v-if="loadingMessages" class="flex items-center justify-center py-8">
            <div class="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          </div>

          <template v-else>
            <div
              v-for="message in messages"
              :key="message.id"
              class="flex gap-2"
              :class="message.authorId === user?.uid ? 'flex-row-reverse' : ''"
            >
              <!-- 아바타 (상대방만) -->
              <div
                v-if="message.authorId !== user?.uid"
                class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-bold"
                :style="{ backgroundColor: getAvatarColor(message.authorId), color: 'white' }"
              >
                {{ getInitial(message.authorEmail) }}
              </div>

              <div :class="message.authorId === user?.uid ? 'items-end' : 'items-start'" class="flex flex-col max-w-[70%]">
                <!-- 이메일 (상대방만, 마스킹 처리) -->
                <span v-if="message.authorId !== user?.uid" class="text-[11px] text-slate-400 mb-0.5 ml-1">
                  {{ maskEmail(message.authorEmail) }}
                </span>
                <!-- 메시지 버블 -->
                <div
                  class="px-3 py-2 rounded-2xl text-[13px]"
                  :class="message.authorId === user?.uid
                    ? 'bg-indigo-600 text-white rounded-br-md'
                    : 'bg-slate-100 text-slate-900 rounded-bl-md'"
                >
                  {{ message.content }}
                </div>
                <span class="text-[10px] text-slate-300 mt-0.5 mx-1">
                  {{ formatTime(message.createdAt) }}
                </span>
              </div>
            </div>

            <!-- 환영 메시지 -->
            <div class="flex justify-center mb-3">
              <div class="px-3 py-1.5 bg-indigo-50 rounded-xl">
                <p class="text-[11px] text-indigo-600 font-medium text-center">
                  물어다줄개 파트너스 여러분, 함께 채팅을 즐겨보세요!
                </p>
              </div>
            </div>

            <div v-if="messages.length === 0" class="text-center py-6">
              <p class="text-[13px] text-slate-400">아직 메시지가 없습니다</p>
              <p class="text-[12px] text-slate-300 mt-1">첫 인사를 남겨보세요!</p>
            </div>
          </template>
        </div>

        <!-- 입력창 -->
        <form @submit.prevent="sendMessage" class="p-3 border-t border-slate-100">
          <div class="flex gap-2">
            <input
              v-model="newMessage"
              type="text"
              placeholder="메시지를 입력하세요..."
              class="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-[13px] font-medium text-slate-900 placeholder:text-slate-400 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition"
              :disabled="sending"
            />
            <button
              type="submit"
              :disabled="!newMessage.trim() || sending"
              class="w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
              </svg>
            </button>
          </div>
        </form>
      </div>
    </transition>

    <!-- 플로팅 버튼 -->
    <button
      @click="toggleChat"
      class="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center relative"
    >
      <svg v-if="!isOpen" class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
      </svg>
      <svg v-else class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
      </svg>

      <!-- 새 메시지 알림 -->
      <span
        v-if="unreadCount > 0 && !isOpen"
        class="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center"
      >
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
    </button>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { collection, query, orderBy, limit, addDoc, onSnapshot, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'

export default {
  name: 'PartnersChatFloat',
  setup() {
    const { user, userProfile } = useAuth()

    const isOpen = ref(false)
    const messages = ref([])
    const newMessage = ref('')
    const loadingMessages = ref(true)
    const sending = ref(false)
    const unreadCount = ref(0)
    const messagesContainer = ref(null)

    let unsubscribe = null

    // 이메일 마스킹 (te**@gmail.com 형태)
    const maskEmail = (email) => {
      if (!email) return '익명'
      const [localPart, domain] = email.split('@')
      if (!localPart || !domain) return '익명'

      if (localPart.length <= 2) {
        return `${localPart}**@${domain}`
      }
      return `${localPart.slice(0, 2)}**@${domain}`
    }

    // 채팅 토글
    const toggleChat = () => {
      isOpen.value = !isOpen.value
      if (isOpen.value) {
        unreadCount.value = 0
        nextTick(() => scrollToBottom())
      }
    }

    // 메시지 실시간 구독 (자유방과 연동)
    const subscribeMessages = () => {
      const q = query(
        collection(db, 'partnerChat_free'),
        orderBy('createdAt', 'desc'),
        limit(50)
      )

      unsubscribe = onSnapshot(q, (snapshot) => {
        const newMessages = []
        snapshot.forEach((doc) => {
          newMessages.push({
            id: doc.id,
            ...doc.data()
          })
        })

        // 새 메시지 알림 (창이 닫혀있고 내가 보낸 게 아닌 경우)
        if (!isOpen.value && messages.value.length > 0 && newMessages.length > messages.value.length) {
          const latestMessage = newMessages[0]
          if (latestMessage.authorId !== user.value?.uid) {
            unreadCount.value++
          }
        }

        messages.value = newMessages.reverse()
        loadingMessages.value = false

        nextTick(() => scrollToBottom())
      }, (error) => {
        console.error('메시지 구독 에러:', error)
        loadingMessages.value = false
      })
    }

    // 메시지 전송
    const sendMessage = async () => {
      if (!newMessage.value.trim() || sending.value || !user.value) return

      const content = newMessage.value.trim()
      newMessage.value = ''
      sending.value = true

      try {
        await addDoc(collection(db, 'partnerChat_free'), {
          content,
          authorId: user.value.uid,
          authorEmail: userProfile.value?.email || user.value.email || '익명',
          authorName: userProfile.value?.name || null,
          createdAt: serverTimestamp()
        })
      } catch (error) {
        console.error('메시지 전송 실패:', error)
        newMessage.value = content
      } finally {
        sending.value = false
      }
    }

    // 스크롤 맨 아래로
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    }

    // 시간 포맷
    const formatTime = (timestamp) => {
      if (!timestamp) return ''
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    }

    // 이니셜 (이메일 첫 글자)
    const getInitial = (email) => {
      if (!email) return '?'
      return email.charAt(0).toUpperCase()
    }

    // 아바타 색상 (userId 기반으로 일관된 색상)
    const getAvatarColor = (userId) => {
      const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6']
      if (!userId) return colors[0]
      const index = userId.charCodeAt(0) % colors.length
      return colors[index]
    }

    onMounted(() => {
      subscribeMessages()
    })

    onUnmounted(() => {
      if (unsubscribe) unsubscribe()
    })

    return {
      user,
      isOpen,
      messages,
      newMessage,
      loadingMessages,
      sending,
      unreadCount,
      messagesContainer,
      maskEmail,
      toggleChat,
      sendMessage,
      formatTime,
      getInitial,
      getAvatarColor
    }
  }
}
</script>

<style scoped>
.chat-popup-enter-active,
.chat-popup-leave-active {
  transition: all 0.2s ease;
}

.chat-popup-enter-from,
.chat-popup-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}
</style>
